/**
 * Dashboard amber banner that fires when an org has used ≥80% of
 * their AI credits this cycle. Visible only to SUPER_ADMIN and
 * HR_ADMIN — employees / managers don't pay the bill and seeing
 * "credits low" copy at the top of their dashboard would be
 * confusing noise.
 *
 * Visibility decisions:
 *   * Hidden for EMPLOYEE / MANAGER (no need to surface ops data).
 *   * Hidden for internal orgs (we never gate ourselves).
 *   * Hidden while still loading the balance.
 *   * Hidden when monthly usage < 80%.
 *
 * The banner is dismissible — once an admin closes it for a given
 * cycle, we suppress it via sessionStorage so it doesn't keep
 * popping back. Reset on next month's reset (the key includes
 * the reset_date).
 */
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Sparkles, X } from "lucide-react";

import {
  getCreditBalance,
  type CreditBalance,
} from "@/api/billing";
import { useAuth } from "@/hooks/useAuth";
import { cn } from "@/lib/utils";

const DISMISS_KEY_PREFIX = "lenavio.lowCredit.dismissed";
const WARN_THRESHOLD_PCT = 80;

function dismissedKeyForCycle(resetDate: string): string {
  return `${DISMISS_KEY_PREFIX}.${resetDate}`;
}

export function LowCreditBanner() {
  const { user } = useAuth();
  const role = user?.role ?? "EMPLOYEE";
  const canSee = role === "SUPER_ADMIN" || role === "HR_ADMIN";
  const canPurchase = role === "SUPER_ADMIN";

  const [balance, setBalance] = useState<CreditBalance | null>(null);
  const [dismissed, setDismissed] = useState<boolean>(false);

  useEffect(() => {
    if (!canSee) return;
    let cancelled = false;
    // Best-effort — a failure on this lightweight endpoint never
    // surfaces to the user. We just hide the banner.
    getCreditBalance()
      .then((b) => {
        if (cancelled) return;
        setBalance(b);
        try {
          const k = dismissedKeyForCycle(b.reset_date);
          setDismissed(
            window.sessionStorage.getItem(k) === "1",
          );
        } catch {
          /* sessionStorage disabled — no dismissal persistence */
        }
      })
      .catch(() => {
        if (!cancelled) setBalance(null);
      });
    return () => {
      cancelled = true;
    };
  }, [canSee]);

  if (!canSee || !balance) return null;
  if (balance.is_internal) return null;
  if (dismissed) return null;
  // Compute pct against the monthly cycle only — purchased balance
  // is a separate, never-expiring pool and we don't want a heavy
  // purchase to mask high monthly burn.
  const monthly = Math.max(0, balance.monthly_allowance);
  if (monthly === 0) return null;
  const pctUsed = Math.min(
    100,
    Math.round((Math.max(0, balance.monthly_used) / monthly) * 100),
  );
  if (pctUsed < WARN_THRESHOLD_PCT) return null;

  const onDismiss = () => {
    setDismissed(true);
    try {
      window.sessionStorage.setItem(
        dismissedKeyForCycle(balance.reset_date),
        "1",
      );
    } catch {
      /* ignore */
    }
  };

  return (
    <div
      role="status"
      aria-live="polite"
      className={cn(
        "flex items-start justify-between gap-3 rounded-lg border px-4 py-3 text-sm",
        pctUsed >= 95
          ? "border-danger/40 bg-danger/10"
          : "border-warning/40 bg-warning/10",
      )}
    >
      <div className="flex items-start gap-2">
        <Sparkles className="h-4 w-4 shrink-0 mt-0.5 text-warning" />
        <p className="text-text-primary">
          <strong>
            Running low on AI credits ({balance.monthly_remaining}{" "}
            remaining).
          </strong>{" "}
          Your modules still work normally — credits power the
          command bar shortcut.
          {canPurchase
            ? " Buy more credits "
            : " Ask a SUPER_ADMIN to buy more "}
          {canPurchase && (
            <Link
              to="/settings/billing"
              className="font-semibold text-warning underline-offset-2 hover:underline"
            >
              →
            </Link>
          )}
        </p>
      </div>
      <div className="flex items-center gap-2 shrink-0">
        {canPurchase && (
          <Link
            to="/settings/billing"
            className="rounded-md bg-warning px-3 py-1.5 text-xs font-semibold text-white hover:bg-warning/90"
          >
            Manage credits
          </Link>
        )}
        <button
          type="button"
          onClick={onDismiss}
          aria-label="Dismiss banner"
          className="text-text-muted hover:text-text-primary"
        >
          <X className="h-4 w-4" />
        </button>
      </div>
    </div>
  );
}
